import { AuthContext } from '@repo-level/core-front';
import { ButtonCross } from 'components/buttons';
import { ScTooltipGlobal } from 'components/tooltip/styled';
import { TabBarContext } from 'contexts/tabBarSize';
import { AnimatePresence, motion } from 'framer-motion';
import { useTheme } from 'hooks/theme';
import type { FC } from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TooltipTypes, useGlobalTooltipStore } from '../store';

const TOOLTIP_REMOVE_TIMEOUT = 7000; // 7 seconds

export const GlobalTooltip: FC = () => {
  const { t } = useTranslation();

  const tooltip = useGlobalTooltipStore((state) => state.tooltip);
  const remove = useGlobalTooltipStore((state) => state.remove);

  const {
    successIcon: SuccessIcon,
    infoIcon: InfoIcon,
    warningIcon: WarningIcon,
    errorIcon: ErrorIcon,
  } = useTheme();

  const { isAuthenticated } = useContext(AuthContext);

  const { tabBarHeight } = useContext(TabBarContext);

  const [timerSetTimeout, setTimerSetTimeout] = useState<NodeJS.Timeout>();

  const startRemovingProcess = () => {
    const timeout = setTimeout(() => {
      remove();
    }, TOOLTIP_REMOVE_TIMEOUT);

    setTimerSetTimeout(timeout);
  };

  const stopRemovingProcess = () => {
    if (timerSetTimeout == null) return;

    clearTimeout(timerSetTimeout);
    setTimerSetTimeout(undefined);
  };

  useEffect(() => {
    if (tooltip && tooltip.animate) {
      startRemovingProcess();

      return () => {
        stopRemovingProcess();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tooltip]);

  const { title, text, icon } = useMemo(() => {
    let title, text;

    if (tooltip == null)
      return { title: undefined, text: undefined, icon: undefined };

    if (tooltip.translate) {
      if (typeof tooltip.title === 'function') {
        title = tooltip.title(t);
      } else if (typeof tooltip.title === 'string') {
        title = t(tooltip.title);
      }
    } else {
      title = tooltip.title;
    }

    if (tooltip.translate) {
      if (typeof tooltip.text === 'function') {
        text = tooltip.text(t);
      } else if (typeof tooltip.text === 'string') {
        text = t(tooltip.text);
      }
    } else {
      text = tooltip.text;
    }

    const icon = {
      [TooltipTypes.SUCCESS]: <SuccessIcon />,
      [TooltipTypes.INFO]: <InfoIcon />,
      [TooltipTypes.WARNING]: <WarningIcon />,
      [TooltipTypes.ERROR]: <ErrorIcon />,
    }[tooltip.type];

    return { title, text, icon };
  }, [tooltip, t, SuccessIcon, InfoIcon, WarningIcon, ErrorIcon]);

  return (
    <AnimatePresence>
      {tooltip != null && (
        <ScTooltipGlobal
          animate={{ opacity: 1, x: 0, transform: 'none' }}
          as={motion.div}
          bottom={tabBarHeight}
          error={tooltip.type === TooltipTypes.ERROR}
          exit={{ opacity: 0, x: '120%', transform: 'none' }}
          info={tooltip.type === TooltipTypes.INFO}
          initial={{ opacity: 0, x: '120%', transform: 'none' }}
          isAuthenticated={isAuthenticated}
          key={tooltip.id}
          onClick={remove}
          onMouseEnter={stopRemovingProcess}
          onMouseLeave={startRemovingProcess}
          success={tooltip.type === TooltipTypes.SUCCESS}
          transition={{ duration: 0.3, delay: 0.2 }}
          warning={tooltip.type === TooltipTypes.WARNING}
        >
          <ButtonCross onClick={remove} right={8} small top={8} />
          <div className="icon">{tooltip.icon ?? icon}</div>
          <div className="title">{title}</div>
          <div className="text">{text}</div>
          {tooltip.button && <div className="button">{tooltip.button}</div>}
        </ScTooltipGlobal>
      )}
    </AnimatePresence>
  );
};
